import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useMemo } from 'react';
import socketService from '../../services/socketService';
import notificationService from '../services/notificationService';
import soundService from '../services/soundService';
import UserLocal from '../../configuracao/userLocal';
import ObligatoryModal from '../components/ObligatoryModal';

// Action original do sistema - dispatch via store global (sem connect)
import { clearAlertaErro } from '../../controlador_erro/erroActions';

// Estado inicial
const initialState = {
  // Notificações do sininho
  bellNotifications: [],
  bellCount: 0,
  
  // Notificações do modal obrigatório
  modalNotifications: [],
  showModal: false,
  
  // Configurações do usuário
  userPreferences: {
    alerta_tela: 0,
    filtrar_notificacao: 0
  },
  
  // Estado da conexão
  isConnected: false,
  lastUpdate: Date.now(),
  
  // Loading states
  isLoading: false,
  isMarkingAsRead: false
};

// Actions
const ACTIONS = {
  SET_BELL_NOTIFICATIONS: 'SET_BELL_NOTIFICATIONS',
  SET_MODAL_NOTIFICATIONS: 'SET_MODAL_NOTIFICATIONS', 
  ADD_NEW_NOTIFICATIONS: 'ADD_NEW_NOTIFICATIONS',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  CLEAR_ALL_NOTIFICATIONS: 'CLEAR_ALL_NOTIFICATIONS',
  SET_MODAL_VISIBILITY: 'SET_MODAL_VISIBILITY',
  TRANSFER_BELL_TO_MODAL: 'TRANSFER_BELL_TO_MODAL',
  TRANSFER_MODAL_TO_BELL: 'TRANSFER_MODAL_TO_BELL',
  SET_USER_PREFERENCES: 'SET_USER_PREFERENCES',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  SET_LOADING: 'SET_LOADING',
  SET_MARKING_AS_READ: 'SET_MARKING_AS_READ',
  UPDATE_TIMESTAMP: 'UPDATE_TIMESTAMP'
};

// Reducer
function notificationReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_BELL_NOTIFICATIONS:
      return {
        ...state,
        bellNotifications: action.payload,
        bellCount: action.payload.length,
        lastUpdate: Date.now()
      };

    case ACTIONS.SET_MODAL_NOTIFICATIONS:
      return {
        ...state,
        modalNotifications: action.payload,
        lastUpdate: Date.now()
      };

    case ACTIONS.ADD_NEW_NOTIFICATIONS:
      const { newNotifications, shouldShowModal } = action.payload;
      
      if (shouldShowModal) {
        // Modal obrigatório - limpar sininho
        return {
          ...state,
          modalNotifications: newNotifications,
          bellNotifications: [],
          bellCount: 0,
          showModal: true,
          lastUpdate: Date.now()
        };
      } else {
        // Sininho - manter modal se já aberto
        return {
          ...state,
          bellNotifications: newNotifications,
          bellCount: newNotifications.length,
          lastUpdate: Date.now()
        };
      }

    case ACTIONS.REMOVE_NOTIFICATION:
      const { notificationKey, fromModal } = action.payload;
      
      if (fromModal) {
        const updatedModal = state.modalNotifications.filter(n => 
          notificationService.getErrorKey(n) !== notificationKey
        );
        
        return {
          ...state,
          modalNotifications: updatedModal,
          showModal: updatedModal.length > 0, // Auto-fecha se vazio
          lastUpdate: Date.now()
        };
      } else {
        const updatedBell = state.bellNotifications.filter(n =>
          notificationService.getErrorKey(n) !== notificationKey
        );
        
        return {
          ...state,
          bellNotifications: updatedBell,
          bellCount: updatedBell.length,
          lastUpdate: Date.now()
        };
      }

    case ACTIONS.CLEAR_ALL_NOTIFICATIONS:
      return {
        ...state,
        bellNotifications: [],
        bellCount: 0,
        modalNotifications: [],
        showModal: false,
        lastUpdate: Date.now()
      };

    case ACTIONS.SET_MODAL_VISIBILITY:
      return {
        ...state,
        showModal: action.payload
      };

    case ACTIONS.TRANSFER_BELL_TO_MODAL:
      return {
        ...state,
        modalNotifications: [...state.bellNotifications],
        bellNotifications: [],
        bellCount: 0,
        showModal: true,
        lastUpdate: Date.now()
      };

    case ACTIONS.TRANSFER_MODAL_TO_BELL:
      return {
        ...state,
        bellNotifications: [...state.modalNotifications],
        bellCount: state.modalNotifications.length,
        modalNotifications: [],
        showModal: false,
        lastUpdate: Date.now()
      };

    case ACTIONS.SET_USER_PREFERENCES:
      return {
        ...state,
        userPreferences: { ...state.userPreferences, ...action.payload }
      };

    case ACTIONS.SET_CONNECTION_STATUS:
      return {
        ...state,
        isConnected: action.payload
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case ACTIONS.SET_MARKING_AS_READ:
      return {
        ...state,
        isMarkingAsRead: action.payload
      };

    case ACTIONS.UPDATE_TIMESTAMP:
      return {
        ...state,
        lastUpdate: Date.now()
      };

    default:
      return state;
  }
}

// Context
const NotificationContext = createContext();

// Provider - sem connect Redux para não re-renderizar com cada action global
function NotificationProviderInternal({ children }) {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const stateRef = useRef(state);
  
  // Manter referência atual do estado
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const dispatchClearAlerta = useCallback((notifications) => {
    if (typeof window !== 'undefined' && window.store) {
      return window.store.dispatch(clearAlertaErro(notifications));
    }
    return Promise.resolve();
  }, []);

  // Carregar configurações do usuário
  useEffect(() => {
    const alerta_tela = UserLocal.getUserConfig('alerta_tela') || 0;
    const filtrar_notificacao = UserLocal.getUserConfig('filtrar_notificacao') || 0;
    
    dispatch({
      type: ACTIONS.SET_USER_PREFERENCES,
      payload: { alerta_tela, filtrar_notificacao }
    });
  }, []);

  // Carregar notificações do localStorage na inicialização
  useEffect(() => {
    const savedNotifications = notificationService.loadFromLocalStorage();
    if (savedNotifications.length > 0) {
      // Usar UserLocal diretamente para evitar race conditions
      const alerta_tela = UserLocal.getUserConfig('alerta_tela') || 0;
      const shouldShowModal = alerta_tela === 1;
      
      dispatch({
        type: ACTIONS.ADD_NEW_NOTIFICATIONS,
        payload: {
          newNotifications: savedNotifications,
          shouldShowModal
        }
      });
    }
  }, []); // Executar apenas uma vez na inicialização

  // Handler para novos erros via WebSocket - ESTÁVEL
  const handleErrorListUpdate = useCallback((erros, tipoEvento) => {
    if (!Array.isArray(erros)) return;

    try {
      const currentState = stateRef.current;
      const currentNotifications = currentState.userPreferences.alerta_tela === 1 
        ? currentState.modalNotifications 
        : currentState.bellNotifications;

      const result = notificationService.processErrorList(erros, currentNotifications);
      
      // Evitar re-render se lista não mudou
      const currentKeys = currentNotifications.map(n => notificationService.getErrorKey(n)).sort().join('|');
      const newKeys = result.allNotifications.map(n => notificationService.getErrorKey(n)).sort().join('|');
      if (currentKeys === newKeys && tipoEvento !== 'refresh_pos_leitura') {
        return;
      }

      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      
      if (result.hasNewErrors) {
        soundService.playNotificationSound();
      }

      const shouldShowModal = notificationService.shouldShowModal();
      
      dispatch({
        type: ACTIONS.ADD_NEW_NOTIFICATIONS,
        payload: {
          newNotifications: result.allNotifications,
          shouldShowModal
        }
      });

      notificationService.saveToLocalStorage(result.allNotifications);

    } catch (error) {
      console.error('Erro ao processar lista de erros:', error);
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  // Setup WebSocket
  useEffect(() => {
    if (!socketService.connected) {
      socketService.connect();
    }
    socketService.tryAuthenticate();

    socketService.addEventListener('lista_erros_atualizada', handleErrorListUpdate);

    const updateConnectionStatus = () => {
      dispatch({
        type: ACTIONS.SET_CONNECTION_STATUS,
        payload: socketService.connected
      });
    };

    const statusInterval = setInterval(updateConnectionStatus, 30000);
    updateConnectionStatus();

    return () => {
      socketService.removeEventListener('lista_erros_atualizada', handleErrorListUpdate);
      clearInterval(statusInterval);
    };
  }, [handleErrorListUpdate]);

  // Marcar notificação como lida
  const markAsRead = useCallback(async (notification, fromModal = false) => {
    dispatch({ type: ACTIONS.SET_MARKING_AS_READ, payload: true });

    try {
      // Usar referência estável da action Redux
      await notificationService.markAsRead(notification, dispatchClearAlerta);

      // Remover do estado
      const notificationKey = notificationService.getErrorKey(notification);
      dispatch({
        type: ACTIONS.REMOVE_NOTIFICATION,
        payload: { notificationKey, fromModal }
      });

      // Atualizar localStorage usando referência atual
      const currentState = stateRef.current;
      const remainingNotifications = fromModal 
        ? currentState.modalNotifications.filter(n => notificationService.getErrorKey(n) !== notificationKey)
        : currentState.bellNotifications.filter(n => notificationService.getErrorKey(n) !== notificationKey);
      
      notificationService.saveToLocalStorage(remainingNotifications);

      // Refresh da API
      const refreshedNotifications = await notificationService.refreshNotifications();
      handleErrorListUpdate(refreshedNotifications, 'refresh_pos_leitura');

    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    } finally {
      dispatch({ type: ACTIONS.SET_MARKING_AS_READ, payload: false });
    }
  }, []); // SEM dependências - usa refs estáveis

  // Marcar todas como lidas
  const markAllAsRead = useCallback(async (fromModal = false) => {
    const currentState = stateRef.current;
    const notifications = fromModal ? currentState.modalNotifications : currentState.bellNotifications;
    
    if (notifications.length === 0) return;

    dispatch({ type: ACTIONS.SET_MARKING_AS_READ, payload: true });

    try {
      // Usar referência estável da action Redux
      await notificationService.markAsRead(notifications, dispatchClearAlerta);

      // Limpar estado
      if (fromModal) {
        dispatch({
          type: ACTIONS.SET_MODAL_NOTIFICATIONS,
          payload: []
        });
        dispatch({
          type: ACTIONS.SET_MODAL_VISIBILITY,
          payload: false
        });
      } else {
        dispatch({
          type: ACTIONS.SET_BELL_NOTIFICATIONS,
          payload: []
        });
      }

      // Limpar localStorage
      notificationService.saveToLocalStorage([]);

      // Refresh da API
      const refreshedNotifications = await notificationService.refreshNotifications();
      handleErrorListUpdate(refreshedNotifications, 'refresh_pos_leitura');

    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    } finally {
      dispatch({ type: ACTIONS.SET_MARKING_AS_READ, payload: false });
    }
  }, []); // SEM dependências - usa refs estáveis

  // Abrir modal manualmente (do sininho)
  const openModal = useCallback(() => {
    const currentState = stateRef.current;
    
    if (currentState.bellNotifications.length > 0) {
      // Transferir notificações do sininho para modal
      dispatch({
        type: ACTIONS.TRANSFER_BELL_TO_MODAL
      });
    } else {
      // Só abrir modal vazio
      dispatch({
        type: ACTIONS.SET_MODAL_VISIBILITY,
        payload: true
      });
    }
  }, []);

  // Fechar modal (só se não tiver notificações obrigatórias)
  const closeModal = useCallback(() => {
    const currentState = stateRef.current;
    if (currentState.userPreferences.alerta_tela === 1 && currentState.modalNotifications.length > 0) {
      // Modal obrigatório - não pode fechar com notificações pendentes
      return false;
    }
    
    // Se há notificações no modal e não é obrigatório, transferir de volta para sininho
    if (currentState.modalNotifications.length > 0) {
      dispatch({
        type: ACTIONS.TRANSFER_MODAL_TO_BELL
      });
    } else {
      // Se não há notificações, só fechar
      dispatch({
        type: ACTIONS.SET_MODAL_VISIBILITY,
        payload: false
      });
    }
    return true;
  }, []);

  const value = useMemo(() => ({
    ...state,
    markAsRead,
    markAllAsRead,
    openModal,
    closeModal,
    dispatch,
    ACTIONS
  }), [state, markAsRead, markAllAsRead, openModal, closeModal]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ObligatoryModal />
    </NotificationContext.Provider>
  );
}

// Hook personalizado
export function useNotifications() {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de NotificationProvider');
  }
  
  return context;
}

// Export do Provider (sem connect Redux)
export const NotificationProvider = NotificationProviderInternal;

export default NotificationContext;